/**
 * A7 · লগইন ও পুনরুদ্ধার — `/auth/login`
 * Frame: `Oprappo P0 Screens.dc.html`, "A7 · লগইন ও পুনরুদ্ধার".
 *
 * Frame note: "No password in P0. When the number itself is gone, the path ends at a
 * person — stated up front so nobody discovers it by getting stuck."
 *
 * CORRECTION (Bangla digits in Manrope): the number field is drawn
 * `500 18px/1 Manrope,sans-serif` carrying a number in Bangla numerals. Manrope has no
 * Bangla digits, so the string falls back silently at the wrong size (docs/02 §
 * numerals). Set in Anek Bangla at the drawn 18px; 18px is under DISPLAY_BAND_PX so the
 * leading goes to the measured 1.75 band. Listed in the WP-C report.
 *
 * ── THE SAME DOOR AS A3 (W2-12) ──────────────────────────────────────────────
 *
 * "কোড পাঠান" here posts to `/v1/auth/otp/request` exactly as A3's does, through the one
 * implementation both screens now share (`door.tsx`): the channel choice, Telegram's step,
 * the Turnstile wait and every answer to a failure. Recorded as a departure from the frame
 * in `docs/deviations/W2-12.md`: the field starts EMPTY and focused with a format
 * placeholder (the frame draws it pre-filled with a sample number); the channel choice
 * sits under it; a failure answers in a line above the button.
 *
 * The two links under the rule now GO somewhere — the frame drew neither destination —
 * and where they go is `/auth/help` (`recovery.tsx`). Today that screen says on arrival
 * that requests are not taken here yet: no Worker route receives a signed-out recovery
 * request, so the form behind it (নম্বর বদলে গেছে? with the old-number field, সহায়তা নিন
 * without it) is not drawn at all. See that file's header and
 * `docs/deviations/W2-12.md` § what no route serves yet.
 *
 * ── WHERE A RETURN LANDS ─────────────────────────────────────────────────────
 *
 * `?next=` — set by the sign-in door (`auth/SignInPrompt.tsx`) to the page that asked — is
 * carried through A4 and followed after the code is verified, once `safeNext` has checked
 * it is a path in this app. Without it, home.
 *
 * ── AND IT IS WHERE A TRUSTED DEVICE RENEWS ITSELF ───────────────────────────
 *
 * docs/07 § 1: an existing user must be able to open the app during a channel outage,
 * because their device is trusted and renewal needs no OTP and no WhatsApp. On mount, if
 * this browser holds a refresh token, it is spent for a fresh session, and a device that
 * is restored goes straight on to `next` — a person whose phone is already trusted has
 * nothing to type here.
 */
import { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@oprappo/ui';
import { hasStoredSession, refreshSession } from '../../auth/session.js';
import { ChannelChoice, DoorNoticeLine, TelegramPanel, safeNext, useCodeDoor } from './door.js';
import { FIELD_ERROR_BOX, FIELD_ERROR_LINE, FIELD_LABEL, Frame, SAND_FRAME, Mark } from './frame.js';
import { RECOVERY_PATH } from './recovery.js';

const WHERE = 'A7 · POST /v1/auth/otp/request';

/** The national format A7 draws its number in, as a shape rather than a number. */
export const A7_PLACEHOLDER = '01XXX XXX XXX';

export function LoginScreen(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const next = safeNext(location.search);
  const door = useCodeDoor({ intent: 'login', next, where: WHERE });

  /** docs/07 § 1 — the renewal that needs no channel. */
  useEffect(() => {
    if (!hasStoredSession()) return;
    let live = true;
    void refreshSession().then((outcome) => {
      if (live && outcome === 'restored') navigate(next ?? '/', { replace: true });
    });
    return () => {
      live = false;
    };
    // `next` is read once, at mount: it is the address this screen was opened with.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (door.frame !== null) return door.frame;

  return (
    <Frame className={`${SAND_FRAME} px-[24px] pt-[32px] pb-[24px] overflow-y-auto`}>
      <Mark size={40} />

      <h1 className="m-0 font-anek font-semibold text-[24px] leading-[1.25] text-ink mt-[34px]">
        আবার স্বাগতম
      </h1>

      <div className="mt-[22px]">
        <label htmlFor="a7-phone" className={FIELD_LABEL}>
          মোবাইল নম্বর
        </label>
        <input
          id="a7-phone"
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          autoFocus
          value={door.number}
          placeholder={A7_PLACEHOLDER}
          aria-invalid={door.fieldError !== null ? true : undefined}
          aria-describedby={door.fieldError !== null ? 'a7-phone-error' : undefined}
          onChange={(e) => door.setNumber(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !door.disabled) door.submit();
          }}
          className={
            'w-full h-[58px] px-[15px] font-anek font-medium text-[18px] leading-[1.75] text-ink ' +
            '[font-variant-numeric:tabular-nums] outline-none rounded-none placeholder:text-muted placeholder:font-normal ' +
            (door.fieldError !== null ? FIELD_ERROR_BOX : 'border border-border bg-white')
          }
        />
        {door.fieldError !== null ? (
          <div id="a7-phone-error" role="alert" className={FIELD_ERROR_LINE + ' mt-[8px]'}>
            {door.fieldError}
          </div>
        ) : null}
      </div>

      {door.options !== null && door.options.length > 1 ? (
        <div className="mt-[18px]">
          <ChannelChoice options={door.options} chosen={door.chosen} onChoose={door.choose} />
        </div>
      ) : null}

      <TelegramPanel step={door.telegram} onReopen={door.reopenTelegram} className="mt-[14px]" />

      <DoorNoticeLine notice={door.notice} className="mt-[14px]" />

      <Button tone="primary" size="lg" className="mt-[20px]" disabled={door.disabled} onClick={door.submit}>
        {door.primaryLabel}
      </Button>

      {/*
        `border-top:1px solid #E2DED5; margin-top:28px; padding-top:20px;
         display:flex; flex-direction:column; gap:16px` — two links, both `500 15px/1.75`
        in #12626F, now pointing at the recovery request.
      */}
      <div className="border-t border-border mt-[28px] pt-[20px] flex flex-col gap-[16px] items-start">
        <Link
          to={`${RECOVERY_PATH}?topic=number`}
          className="font-anek font-medium text-[15px] leading-[1.75] text-link"
        >
          নম্বর বদলে গেছে?
        </Link>
        <Link to={RECOVERY_PATH} className="font-anek font-medium text-[15px] leading-[1.75] text-link">
          সহায়তা নিন
        </Link>
      </div>

      <div className="flex-1 min-h-[16px]" />

      {/*
        The challenge, in the same shape A3 gives it: `interaction-only`, so nothing is
        drawn unless Cloudflare wants an interaction, and `empty:hidden`, so a build
        with no sitekey inserts nothing and the container occupies no space at all.
      */}
      <div ref={door.challenge} className="empty:hidden flex" />

      <div className="border border-border bg-sand px-[15px] py-[13px]">
        <div className="font-anek font-normal text-[14px] leading-[1.75] text-body">
          নম্বর হারিয়ে গেলে পরিচয় যাচাই করে একজন মানুষ আপনাকে সাহায্য করবেন।
        </div>
      </div>
    </Frame>
  );
}
